import {
  ALPHA,
  GRADE_SCALE_TOOL,
  SCORE,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
} from "Constants/stringConstants";
import _ from "lodash";

export const compareInScaledRange = ({ value, fromRange, toRange }) => {
  return (
    ((value - fromRange?.min) * (toRange?.max - toRange?.min)) /
      (fromRange?.max - fromRange?.min) +
    toRange?.min
  );
};

export const getGradeScaleMinMaxValues = gradeScaleValues => {
  if (_.isEmpty(gradeScaleValues)) {
    return { minValue: null, maxValue: null };
  }

  const minValue = _.minBy(gradeScaleValues, "minValue")?.minValue;
  const maxValue = _.maxBy(gradeScaleValues, "maxValue")?.maxValue;

  return { minValue, maxValue };
};

export const isAssessmentToolPreviewAvailable = ({ type }) => {
  return !_.includes(["ANECDOTE", "EXEMPLAR"], type);
};

export const getIsToolPresent = (assessmentTools, types) => {
  const _assessmentTools = assessmentTools?.edges || assessmentTools;

  return _.some(_assessmentTools, ({ node }) =>
    _.includes(_.castArray(types), node?.assessmentToolType)
  );
};

const SCORING_TOOL_TYPES = [SCORE_BASED_RUBRIC, STANDARD_BASED_RUBRIC];

/**
 If any of the assessment tools present is a scoring tool (i.e. uses numeric scores), 
 this function returns true. Used to determine whether task weight field should be disabled,
 as task weight doesn't apply when a scoring tool is present since the score itself 
 determines the grade scale rating.
 */
export const getIsScoringToolPresent = assessmentTools => {
  const _assessmentTools = assessmentTools?.edges || assessmentTools;

  return _.some(_assessmentTools, ({ node }) => {
    const toolType = node?.assessmentToolType;
    const isMatchingType = _.includes(SCORING_TOOL_TYPES, toolType);

    if (!isMatchingType) return false;

    // Only STANDARD_BASED_RUBRIC can have ALPHA grading type,
    // which doesn't use numeric scores — skip disabling for that case.
    if (toolType === STANDARD_BASED_RUBRIC) {
      const isAlphaGrading =
        _.get(node, "assessmentToolItem.gradeScale.gradingType") === ALPHA;
      return !isAlphaGrading;
    }

    return true;
  });
};

export const getLabelBeforeColon = text => {
  const match = text.trim().match(/^([^:]+):/);
  return match ? match[1].trim() : null;
};

export const getAssessmentToolItem = (assessmentTools, type) => {
  const tool = _.find(assessmentTools?.edges || assessmentTools, ({ node }) => {
    return node?.assessmentToolType === type;
  });

  return _.get(tool, "node.assessmentToolItem") || {};
};

export const getGradeScaleValuesFromGradeScaleTool = assessmentTools => {
  const gradeScaleTool = getAssessmentToolItem(
    assessmentTools,
    GRADE_SCALE_TOOL
  );

  return _.flow([
    node => _.get(node, "gradeScaleValues", []),
    values => _.orderBy(values, "displaySequence"),
    values =>
      _.map(values, gradeScaleValue => {
        const { id, abbreviation, gradeScaleValueScores } =
          gradeScaleValue || {};
        const { minValue, maxValue, gradeValue } =
          _.head(gradeScaleValueScores) || {};

        return { id, abbreviation, minValue, maxValue, gradeValue };
      }),
  ])(gradeScaleTool);
};

export const calculateGradeScaleRatingFromOtherTool = params => {
  const { assessmentTools, score } = params;
  let { toolResponses } = params;

  const hasScoreBasedRubric = getIsToolPresent(
    assessmentTools,
    SCORE_BASED_RUBRIC
  );

  const hasStandardBasedRubric = getIsToolPresent(
    assessmentTools,
    STANDARD_BASED_RUBRIC
  );

  const hasScoreTool = getIsToolPresent(assessmentTools, SCORE);

  let toolType = null;

  if (hasStandardBasedRubric) {
    toolType = STANDARD_BASED_RUBRIC;
  } else if (hasScoreBasedRubric) {
    toolType = SCORE_BASED_RUBRIC;
  } else if (hasScoreTool) {
    toolType = SCORE;
  } else {
    return null;
  }

  if (toolType === SCORE) {
    const scoreTool = getAssessmentToolItem(assessmentTools, SCORE);
    const maxScore = _.get(scoreTool, "scoreToolData.maxScore");

    if (_.isNil(score) || score === "") {
      return null;
    }

    const gradeScaleValues = getGradeScaleValuesFromGradeScaleTool(
      assessmentTools
    );
    if (_.isEmpty(gradeScaleValues)) return null;

    const { minValue, maxValue } = getGradeScaleMinMaxValues(gradeScaleValues);

    const gradeValuesAvg = compareInScaledRange({
      value: parseFloat(score),
      fromRange: {
        min: 0,
        max: maxScore,
      },
      toRange: {
        min: minValue,
        max: maxValue,
      },
    });

    return _.find(gradeScaleValues, ({ minValue }) => {
      return minValue <= gradeValuesAvg;
    });
  }

  const rubric = getAssessmentToolItem(assessmentTools, toolType);
  const rubricCriteria = _.get(rubric, "standardRubricCriterias", []);

  const scoreTool = getAssessmentToolItem(assessmentTools, SCORE);

  let maxScore = null;
  if (!_.isEmpty(rubricCriteria)) {
    maxScore = _.get(rubric, "associateScoreTool.scoreToolData.maxScore");
  } else {
    maxScore = _.get(scoreTool, "scoreToolData.maxScore");
  }

  if (toolType === STANDARD_BASED_RUBRIC) {
    const gradingType = _.get(rubric, "gradeScale.gradingType");
    if (gradingType !== "ALPHA" || !rubricCriteria) {
      return null;
    }
  } else if (toolType === SCORE_BASED_RUBRIC && !rubricCriteria) {
    return null;
  }

  const gradeScaleValues = getGradeScaleValuesFromGradeScaleTool(
    assessmentTools
  );
  if (_.isEmpty(gradeScaleValues)) return null;

  if (_.some(toolResponses, response => _.has(response, "toolResponses"))) {
    const { id } = rubric;
    toolResponses = _.get(
      _.find(toolResponses, ({ toolId }) => toolId === id),
      "toolResponses",
      {}
    );
  }

  const gradeValues = _(toolResponses)
    .map(toolResponse => {
      const criterion = _.find(rubricCriteria, { id: toolResponse.rowId });
      const descriptor = _.find(criterion?.descriptors, {
        id: toolResponse.columnId,
      });

      if (toolType === STANDARD_BASED_RUBRIC) {
        const gradeScaleValueId = _.get(descriptor, "gradeScaleValue.id", null);
        if (gradeScaleValueId) {
          const gradeScaleValue = _.find(gradeScaleValues, {
            id: gradeScaleValueId,
          });
          if (gradeScaleValue) {
            return parseFloat(gradeScaleValue.gradeValue || 0);
          }
        }
      } else {
        const score = _.get(descriptor, "score", null);
        if (!_.isNil(score)) {
          return parseFloat(score);
        }
      }

      return null;
    })
    .filter(value => !_.isNil(value) && !_.isNaN(value))
    .value();

  let gradeValuesAvg;

  if (toolType === STANDARD_BASED_RUBRIC) {
    gradeValuesAvg = _.mean(gradeValues);
  } else if (toolType === SCORE_BASED_RUBRIC) {
    const { minValue, maxValue } = getGradeScaleMinMaxValues(gradeScaleValues);
    gradeValuesAvg = compareInScaledRange({
      value: !_.isEmpty(gradeValues) ? _.sum(gradeValues) : score ? score : NaN,
      fromRange: {
        min: 0,
        max: maxScore,
      },
      toRange: {
        min: minValue,
        max: maxValue,
      },
    });
  }

  return _.find(gradeScaleValues, ({ minValue }) => {
    return minValue <= gradeValuesAvg;
  });
};
